<script lang="ts">
import { isAppUsing } from '@@/bits/flip'
import sectionSorter from '@@/bits/section_sorter'
import OzDropIndicatorLine from '@@/library/v4/components/OzDropIndicatorLine.vue'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfaceSectionsStore } from '@@/pinia/surface_sections'
import type { OnDropPayload } from '@@/pragmatic-drag-and-drop/helpers'
import { useDraggableElement } from '@@/pragmatic-drag-and-drop/useDraggableElement'
import { useDropTargetElement } from '@@/pragmatic-drag-and-drop/useDropTargetElement'
import type { LabelledSection, Post } from '@@/types'
import SurfacePostGroupHeaderSection from '@@/vuecomponents/SurfacePostGroupHeaderSection.vue'
import SurfacePostGroupHeaderSectionOld from '@@/vuecomponents/SurfacePostGroupHeaderSectionOld.vue'
import { POST_ROW_DRAGGABLE_TYPE } from '@@/vuecomponents/SurfacePostsTableRow.vue'
import SurfacePostsTableSectionHeaderPreview from '@@/vuecomponents/SurfacePostsTableSectionHeaderPreview.vue'
import { useSectionSorterHelper } from '@@/vuecomposables/section_sorter_helper'
import { storeToRefs } from 'pinia'
import { computed, nextTick, onMounted, ref } from 'vue'

export const SECTION_HEADER_DRAGGABLE_TYPE = 'section-header' as const
export default {}
</script>

<script setup lang="ts">
const props = defineProps<{
  section: Required<LabelledSection>
  hideButtons: boolean
  backgroundLuminance: 'light' | 'dark'
}>()

const emit = defineEmits<{
  (e: 'drag-start', payload: { section: typeof props.section }): void
  (e: 'drag-end', payload: { section: typeof props.section }): void
}>()

const surfaceSectionsStore = useSurfaceSectionsStore()
const { sortedSections } = storeToRefs(surfaceSectionsStore)
const surfacePostsStore = useSurfacePostsStore()
const { canIDragSections } = storeToRefs(useSurfacePermissionsStore())
const containerRef = ref<HTMLDivElement | undefined>(undefined)

// #region section drag and drop
const sectionTitleWidth = ref(0)
const sectionHeaderRef = ref<InstanceType<typeof SurfacePostGroupHeaderSectionOld> | null>(null)
onMounted(async () => {
  await nextTick()

  const titleElement = sectionHeaderRef.value?.sectionTitleRef?.$el
  if (titleElement != null) {
    sectionTitleWidth.value = titleElement.getBoundingClientRect().width
  }
})

const { isDragging } = useDraggableElement({
  elementRef: containerRef,
  type: SECTION_HEADER_DRAGGABLE_TYPE,
  data: computed(() => ({
    section: props.section,
    titleWidth: sectionTitleWidth.value,
  })),
  isDraggable: canIDragSections,
  onDragStart: () => {
    emit('drag-start', { section: props.section })
  },
  dragPreview: SurfacePostsTableSectionHeaderPreview,
  dragPreviewComponentProps: computed(() => ({
    section: props.section,
  })),
  onDrop: () => {
    emit('drag-end', { section: props.section })
  },
})

const { closestEdgeToPointer, addDraggableSource } = useDropTargetElement({
  elementRef: containerRef, // both drag targets and drop targets are the same containerRef element
  type: SECTION_HEADER_DRAGGABLE_TYPE, // only accept drops from other section headers being dragged
  data: computed(() => {
    return {
      section: props.section,
    }
  }),
})

const elementBeingDraggedOverType = ref<
  typeof POST_ROW_DRAGGABLE_TYPE | typeof SECTION_HEADER_DRAGGABLE_TYPE | undefined
>(undefined)

const dropIndicatorLineWidth = ref(0)
addDraggableSource({
  type: SECTION_HEADER_DRAGGABLE_TYPE,
  axis: 'vertical',
  onDragEnter: (e) => {
    if (e.source.data.type !== SECTION_HEADER_DRAGGABLE_TYPE) return
    elementBeingDraggedOverType.value = SECTION_HEADER_DRAGGABLE_TYPE
  },
  onDragLeave: () => {
    elementBeingDraggedOverType.value = undefined
  },
  onDrag: (e) => {
    const sourceData = e.source.data.data as { titleWidth: number }
    dropIndicatorLineWidth.value = sourceData.titleWidth
  },
  onDrop: (e: OnDropPayload<{ section: typeof props.section }, { section: typeof props.section }>) => {
    const sourceSection = e.sourceItem.data.section // section being dragged
    const targetSection = e.targetItem.data.section // section being dropped on
    const targetIndex = sortedSections.value.findIndex((section) => section.id === targetSection.id)

    let previousSection: LabelledSection | undefined
    let nextSection: LabelledSection | undefined

    // dropping before or after the target section
    if (e.relativePositionToTarget === 'before') {
      previousSection = sortedSections.value[targetIndex - 1]
      nextSection = sortedSections.value[targetIndex]
    } else {
      previousSection = sortedSections.value[targetIndex]
      nextSection = sortedSections.value[targetIndex + 1]
    }

    const { sortIndex, gapSize } = (() => {
      // dropping at the start of the list
      if (previousSection == null) {
        const newSortIndex = sectionSorter.getTopSortIndex()
        return {
          sortIndex: newSortIndex,
          gapSize: Math.abs(newSortIndex - sortedSections.value[0].sort_index),
        }
      }

      // dropping at the end of the list
      if (nextSection == null) {
        const newSortIndex = sectionSorter.getBottomSortIndex()
        return {
          sortIndex: newSortIndex,
          gapSize: Math.abs(newSortIndex - sortedSections.value[sortedSections.value.length - 1].sort_index),
        }
      }

      // dropping between two sections
      return useSectionSorterHelper().getNewIndexBetween(previousSection, nextSection)
    })()

    if (!sortIndex || !gapSize) return

    surfaceSectionsStore.sortSection({
      sectionId: Number(sourceSection.id),
      sortIndex,
      gapSize,
    })
  },
})

const postBeingDraggedOver = ref<Post | undefined>(undefined)
addDraggableSource<{ post: Post }>({
  type: POST_ROW_DRAGGABLE_TYPE,
  axis: 'vertical',
  onDragEnter: (e) => {
    if (e.source.data.type !== POST_ROW_DRAGGABLE_TYPE) return
    const post = (e.source.data.data as { post: Post }).post
    postBeingDraggedOver.value = post
    elementBeingDraggedOverType.value = POST_ROW_DRAGGABLE_TYPE
  },
  onDragLeave: () => {
    postBeingDraggedOver.value = undefined
    elementBeingDraggedOverType.value = undefined
  },
  onDrop: (e) => {
    if (sectionBeingDroppedInto.value == null) return

    // move post to new section
    surfacePostsStore.updatePostSection({
      postCid: e.sourceItem.data.post.cid,
      sectionId: Number(sectionBeingDroppedInto.value.id),
    })
  },
})

const dropIndicatorLineStyle = computed(() => {
  if (!dropIndicatorLineWidth.value) return undefined
  return {
    width: `${dropIndicatorLineWidth.value}px`,
  }
})

const sectionBeingDroppedInto = computed(() => {
  if (closestEdgeToPointer.value === 'bottom') {
    return props.section
  }
  if (closestEdgeToPointer.value === 'top') {
    const currentSectionIndex = sortedSections.value.findIndex((section) => section.id === props.section.id)
    if (currentSectionIndex === 0) return undefined
    return sortedSections.value[currentSectionIndex - 1]
  }
  return undefined
})

const showDropIndicatorLine = computed(() => {
  // always show if we're dragging section header
  if (elementBeingDraggedOverType.value === SECTION_HEADER_DRAGGABLE_TYPE) return true

  if (sectionBeingDroppedInto.value == null) return false

  if (elementBeingDraggedOverType.value === POST_ROW_DRAGGABLE_TYPE && sectionBeingDroppedInto.value != null) {
    // dropping post on the same section header > ignore
    if (postBeingDraggedOver.value?.wall_section_id === sectionBeingDroppedInto.value.id) {
      return false
    }
    // dropping post into a non-empty section > ignore, let user drop it on a post in that section instead.
    if (!surfaceSectionsStore.isSectionEmpty(sectionBeingDroppedInto.value.id)) {
      return false
    }
  }
  return true
})
// #endregion
</script>

<template>
  <div ref="containerRef" :class="['relative', 'flex', isDragging && 'opacity-50']">
    <SurfacePostGroupHeaderSection
      v-if="isAppUsing('groupPostsV2')"
      ref="sectionHeaderRef"
      :section="section"
      :hide-buttons="hideButtons"
      :class="['sticky start-0']"
    />
    <SurfacePostGroupHeaderSectionOld
      v-else
      ref="sectionHeaderRef"
      :section="section"
      :hide-buttons="hideButtons"
      :class="['sticky start-0']"
    />
    <OzDropIndicatorLine
      v-if="closestEdgeToPointer && showDropIndicatorLine"
      :edge="closestEdgeToPointer"
      :gap="12"
      :dark-mode="backgroundLuminance === 'dark'"
      :style="dropIndicatorLineStyle"
      :class="['mx-1']"
    />
  </div>
</template>
